/**
 * useDeviceLocation (Card 29)
 *
 * The explicit permission state machine for the service-area picker. It owns
 * three things the UI must never re-derive:
 *
 *   1. WHETHER ASKING IS ALLOWED. `permission` is read without prompting on
 *      mount, and `request()` refuses to fire once it is 'denied'. That is the
 *      "never a repeated prompt" rule enforced in code rather than in copy.
 *   2. THE DIFFERENCE BETWEEN FAILURE MODES. 'denied' is a dead end that needs
 *      device settings; 'timeout'/'unavailable' are retryable; 'no-match' means
 *      we got a perfectly good fix that is outside US coverage. They read very
 *      differently to a user and must not collapse into "something went wrong".
 *   3. RE-RESOLUTION AFTER A PIN DRAG. `resolveCoordinates()` runs the same
 *      backend lookup from a coordinate the user chose instead of one the
 *      device reported, so the map and the button produce identical values.
 *
 * Nothing here is required for the picker to work — the typed path is always
 * live, and every terminal state of this hook leaves it that way.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DeviceCoordinates,
  DeviceLocationError,
  DeviceLocationErrorCode,
  getCurrentPosition,
  getLocationPermissionState,
  isGeolocationSupported,
  LocationPermissionState,
} from '@/lib/geolocation';
import { reverseGeocode, ReverseGeoResult } from '@/lib/location-data';

export type DeviceLocationStatus = 'idle' | 'locating' | 'resolving' | 'ready' | 'error';

/** Adds the two failures that happen AFTER a position was obtained. */
export type DeviceLocationFailure = DeviceLocationErrorCode | 'lookup-failed' | 'no-match';

export interface UseDeviceLocationValue {
  status: DeviceLocationStatus;
  permission: LocationPermissionState;
  /** True while either the device fix or the area lookup is outstanding. */
  isBusy: boolean;
  /** False when the platform has no geolocation at all — hide the control. */
  isSupported: boolean;
  /** True once denied: the control must stop offering to try again. */
  isBlocked: boolean;
  coordinates: DeviceCoordinates | null;
  result: ReverseGeoResult | null;
  error: DeviceLocationFailure | null;
  /** Read the device position, then resolve it to a service area. */
  request: () => Promise<void>;
  /**
   * Open the pin-drop at a coordinate WITHOUT reading the device. This is the
   * "pick on a map" path: it must work when permission is denied or absent,
   * so it never touches geolocation or the permission state.
   */
  openPinAt: (latitude: number, longitude: number) => Promise<void>;
  /** Re-resolve from a coordinate the user picked (map pin drag). */
  resolveCoordinates: (latitude: number, longitude: number) => Promise<void>;
  /** Return to idle, discarding any pending result. */
  reset: () => void;
}

export function useDeviceLocation(): UseDeviceLocationValue {
  const [status, setStatus] = useState<DeviceLocationStatus>('idle');
  const [permission, setPermission] = useState<LocationPermissionState>('unknown');
  const [coordinates, setCoordinates] = useState<DeviceCoordinates | null>(null);
  const [result, setResult] = useState<ReverseGeoResult | null>(null);
  const [error, setError] = useState<DeviceLocationFailure | null>(null);

  const isMounted = useRef(true);
  // Guards against a double-tap firing two platform prompts.
  const inFlight = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Read the permission state on mount WITHOUT prompting, so a user who denied
  // in a previous session sees the explanation rather than a button that will
  // silently do nothing. Re-read on every mount deliberately: they may have
  // turned it back on in device settings since.
  useEffect(() => {
    let cancelled = false;
    getLocationPermissionState()
      .then((state) => {
        if (!cancelled && isMounted.current) setPermission(state);
      })
      .catch(() => {
        if (!cancelled && isMounted.current) setPermission('unknown');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runLookup = useCallback(async (position: DeviceCoordinates) => {
    setStatus('resolving');
    try {
      const lookup = await reverseGeocode(position.latitude, position.longitude);
      if (!isMounted.current) return;
      setResult(lookup);
      if (lookup.match) {
        setStatus('ready');
        setError(null);
      } else {
        // A good fix that we simply cannot name. Distinct from a lookup failure:
        // retrying will not help, so the copy points at the typed path.
        setStatus('error');
        setError('no-match');
      }
    } catch {
      if (!isMounted.current) return;
      setResult(null);
      setStatus('error');
      setError('lookup-failed');
    }
  }, []);

  const request = useCallback(async () => {
    if (inFlight.current) return;
    if (!isGeolocationSupported()) {
      setStatus('error');
      setError('unsupported');
      setPermission('unsupported');
      return;
    }
    // Hard stop: once the platform has told us no, asking again is a no-op that
    // would only re-render a spinner the user cannot resolve from this screen.
    if (permission === 'denied') {
      setStatus('error');
      setError('denied');
      return;
    }

    inFlight.current = true;
    setStatus('locating');
    setError(null);
    setResult(null);

    try {
      const position = await getCurrentPosition();
      if (!isMounted.current) return;
      setCoordinates(position);
      setPermission('granted');
      await runLookup(position);
    } catch (err) {
      if (!isMounted.current) return;
      const code = err instanceof DeviceLocationError ? err.code : 'unavailable';
      setCoordinates(null);
      setStatus('error');
      setError(code);
      if (code === 'denied') setPermission('denied');
      if (code === 'unsupported') setPermission('unsupported');
    } finally {
      inFlight.current = false;
    }
  }, [permission, runLookup]);

  const openPinAt = useCallback(
    async (latitude: number, longitude: number) => {
      setError(null);
      setResult(null);
      setCoordinates({ latitude, longitude, accuracyMeters: null });
      await runLookup({ latitude, longitude, accuracyMeters: null });
    },
    [runLookup],
  );

  const resolveCoordinates = useCallback(
    async (latitude: number, longitude: number) => {
      // A pin drag is a user-chosen point, so there is no device read and no
      // permission involved — only the area lookup.
      setCoordinates({ latitude, longitude, accuracyMeters: null });
      await runLookup({ latitude, longitude, accuracyMeters: null });
    },
    [runLookup],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setCoordinates(null);
    setResult(null);
    setError(null);
  }, []);

  return {
    status,
    permission,
    isBusy: status === 'locating' || status === 'resolving',
    isSupported: permission !== 'unsupported' && isGeolocationSupported(),
    isBlocked: permission === 'denied',
    coordinates,
    result,
    error,
    request,
    openPinAt,
    resolveCoordinates,
    reset,
  };
}

export default useDeviceLocation;
